import axios from 'axios';
import DOMPurify from 'dompurify';
import { getToken } from "../store/utils/storeHelpers";

const isProd = process.env.NODE_ENV === 'production';
const base = isProd 
  ? process.env.VUE_APP_BACKEND_BASE_URL 
  : 'http://localhost:4000';

const BASE_URL = `${base}/api`;

function sanitizeData(data) {
  if (typeof data === 'string') {
    return DOMPurify.sanitize(data);
  }
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }
  if (data !== null && typeof data === 'object' && !(data instanceof Blob) && !(data instanceof File)) {
    const sanitizedObj = {};
    for (const key in data) {
      if (key.toLowerCase().includes('password') || key.toLowerCase().includes('token')) {
        sanitizedObj[key] = data[key];
      } else {
        sanitizedObj[key] = sanitizeData(data[key]);
      }
    }
    return sanitizedObj;
  }
  return data;
}

function sanitizeFormData(formData) {
  const newFormData = new FormData();
  for (let [key, value] of formData.entries()) {
    if (typeof value === 'string') {
      newFormData.append(key, DOMPurify.sanitize(value));
    } else {
      newFormData.append(key, value);
    }
  }
  return newFormData;
}

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 100000,
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

const apiClient2 = axios.create({
  baseURL: BASE_URL,
  responseType: 'blob',
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: { Accept: 'application/xlsx', 'Content-Type': 'application/xlsx' },
});

const apiClient3 = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: { Accept: 'application/xlsx', 'Content-Type': 'multipart/form-data' },
});

const publicRoutes = [
  '/schema/proLoginSchema',
  '/schema/proSignUpSchema',
  '/schema/proResPassSchema',
  '/usersCodeVerifGen',
  '/usersRePass',
  '/auth/login',
  '/auth/register',
  '/public/info',
  'homeProSchema',
  'homePacSchema',
  '/schema/patLoginSchema',
  '/schema/patSignUpSchema',
  '/schema/patResPassSchema',
  '/authPat/login',
  '/authPat/register',
  'mentalCareers',
  'statusLicenses',
  'birthSexes',
  'year',
];

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp && payload.exp < Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
}

[apiClient, apiClient2, apiClient3].forEach((client) => {
  client.interceptors.request.use((config) => {
    
    if (config.data) {
      if (config.data instanceof FormData) {
        config.data = sanitizeFormData(config.data);
      } else {
        config.data = sanitizeData(config.data);
      }
    }

    if (config.headers.Authorization) {
      return config;
    }

    const token = getToken();

    if (token && token !== 'undefined' && token !== 'null') {
      
      if (isTokenExpired(token)) {
        console.error("Sesión expirada.");
        localStorage.removeItem('token'); 
        window.location.href = '/'; 
        return Promise.reject("Token expirado");
      }

      config.headers.Authorization = `Bearer ${token}`;
    } else {
      const isPublic = publicRoutes.some(route => config.url.includes(route));
      if (!isPublic) {
        console.warn("Petición sin token a URL privada:", config.url);
      }
    }
    
    return config;
  }, (error) => Promise.reject(error));
});

const friendlyMessages = {
  400: 'Hubo un error en los datos enviados. Por favor, verifica la información.',
  401: 'Tu sesión ha expirado o no tienes permisos. Por favor, inicia sesión de nuevo.',
  403: 'No tienes autorización para realizar esta acción.',
  404: 'Lo sentimos, no pudimos encontrar lo que buscabas.',
  409: 'Esta información ya existe en nuestro sistema (ej. correo o DNI duplicado).',
  422: 'Hay un problema con la validación de los datos. Revisa los campos resaltados.',
  500: 'Estamos experimentando problemas técnicos en nuestro servidor. Inténtalo más tarde.',
  default: 'Ups, algo salió mal. Por favor, intenta de nuevo en unos momentos.'
};

function handleApiError(error) {
  console.error('Detalle técnico del error:', error.response || error.message);
  
  const status = error?.response?.status;
  const backendMessage = error?.response?.data?.message;
  const userFriendlyMessage = backendMessage || friendlyMessages[status] || friendlyMessages.default;
  error.userMessage = userFriendlyMessage;

  return Promise.reject(error);
}

apiClient.interceptors.response.use((response) => response, handleApiError);

export default {
  // Esquemas y Auth
  homeProSchema: () => apiClient.get('/schema/homeProSchema'),
  homeProLegalSchema: () => apiClient.get('/schema/homeProLegalSchema'),
  proLoginSchema: () => apiClient.get('/schema/proLoginSchema'),
  proLogin: (token) => apiClient.post('/auth/login', {}, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }),
  proSignUpSchema: () => apiClient.get('/schema/proSignUpSchema'),
  proSignUp: (model) => apiClient.post('/auth/register', model ),

  // Catálogos y Otros
  catMentalCareers: () => apiClient.get('/mentalCareers'),
  catStatusLicenses: () => apiClient.get('/statusLicenses'),
  proAuthRegister: () => apiClient.get('/auth/register'),

  // MainHeaderPlatA
  mainHeaderPlatASchema: () => apiClient.get('/schema/mainHeaderPlatASchema'),

  // ProOTP
  proOTPSchema: () => apiClient.get('/schema/proOTPSchema'),

  // ProResPass
  proResPassSchema: () => apiClient.get('/schema/proResPassSchema'),

  // usersCodeVerif
  usersPin: (id, model) => apiClient.post(`/usersPin/${id}`, model),
  usersPass: (id, model) => apiClient.post(`/usersPass/${id}`, model),
  usersRePass: (model) => apiClient.post(`/usersRePass`, model),
  usersCodeVerif: (id, model) => apiClient.post(`/usersCodeVerif/${id}`, model),
  usersCodeVerifGen: (model) => apiClient.post(`/usersCodeVerifGen`, model),
  usersDelAccount: (id, model) => apiClient.post(`/usersDelAccount/${id}`, model),

  // ProHome
  proHomeSchema: () => apiClient.get('/schema/proHomeSchema'),

  // MainHeaderPlatB
  mainHeaderPlatBSchema: () => apiClient.get('/schema/mainHeaderPlatBSchema'),

  // ProLegal
  proLegalSchema: () => apiClient.get('/schema/proLegalSchema'),

  // ProAccount
  proAccountSchema: () => apiClient.get('/schema/proAccountSchema'),
  modelProAccountProfes: (id) => apiClient.get(`/profesAccount/${id}`),
  profesAccount: (id, model) => apiClient.post(`/profesAccount/${id}`, model),

  // ProProfileA
  proProfileASchema: () => apiClient.get('/schema/proProfileASchema'),
  catBirthSexes: () => apiClient.get('/birthSexes'),
  catISO3366_1: () => apiClient.get('/iso3366n1'),
  catDepartments: () => apiClient.get('/departments'),
  catProvinces: () => apiClient.get('/provinces'),
  catDistricts: () => apiClient.get('/districts'),
  modelProProfileA: (id, model) =>
    apiClient.post(`/profes/${id}`, model),
  updateProProfileA: (id, model) =>
    apiClient.put(`/profes/${id}`, model),

  // ProProfileB
  proProfileBSchema: () => apiClient.get('/schema/proProfileBSchema'),
  catStudiesArea: () => apiClient.get('/studiesArea'),
  catCareersArea: () => apiClient.get('/careersArea'),
  catCareers: () => apiClient.get('/careers'),
  catEduInstitutions: () => apiClient.get('/eduInstitutions'),
  catInstructionLevels: () => apiClient.get('/instructionLevels'),
  catInstructionYears: () => apiClient.get('/instructionYears'),
  modelProProfileB: (id) => apiClient.get(`/profesStudies/${id}`),
  createProProfileB: (id, model) =>
    apiClient.post(`/profesStudies/${id}`, model),
  deleteProProfileB: (id) => apiClient.delete(`/profesStudies/${id}`),

  // ProProfileC
  proProfileCSchema: () => apiClient.get('/schema/proProfileCSchema'),
  catThemesGroups: () => apiClient.get('/themesGroups'),
  catThemes: () => apiClient.get('/themes'),
  catThemesAges: () => apiClient.get('/themesAges'),
  catIntensity: () => apiClient.get('/intensity'),
  modelProProfileC: (id) => apiClient.get(`/profesSpec/${id}`),
  createProProfileC: (id, model) =>
    apiClient.post(`/profesSpec/${id}`, model),
  deleteProProfileC: (id) => apiClient.delete(`/profesSpec/${id}`),

  // ProPatientsA
  proPatientsASchema: () => apiClient.get('/schema/proPatientsASchema'),
  createProPatientsA: (model) => apiClient.post(`/professionalHasPatients`, model),

  // ProPatientsB
  proPatientsBSchema: () => apiClient.get('/schema/proPatientsBSchema'),
  modelProPatientsB: (id) => apiClient.get(`/professionalHasPatients/${id}`),
  dataCrudProPatientsB: (id, model) =>
    apiClient.post(`/professionalHasPatients/${id}`, model),

  // ProPatientsC
  proPatientsCSchema: () => apiClient.get('/schema/proPatientsCSchema'),
  modelProPatientsC: (id, model) =>
    apiClient.post(`/patients/${id}`, model),
  updateProPatientsC: (id, model) =>
    apiClient.put(`/patients/${id}`, model),
  dataCrudProPatientsC: (id1, id2) => apiClient.get(`/clinicalHistories/${id1}/${id2}`),
  createClinicalHistories: (newClinicalHistories) =>
    apiClient.post(`/clinicalHistories`, newClinicalHistories),
  proTransfers: (id, model) =>
    apiClient.put(`/proTransfers/${id}`, model),
  proAssimilate: (id, model) =>
    apiClient.put(`/proAssimilate/${id}`, model),
  proSummaryHCP: (id1, id2) => apiClient2.get(`/summaryHCP/${id1}/${id2}`),
  proExportHCP: (id1, id2) => apiClient2.get(`/exportHCP/${id1}/${id2}`),
  proReportHCP: (id1, id2) => apiClient2.get(`/exportReportHCP/${id1}/${id2}`),

  // ProPatientsCReport
  proPatientsCReportSchema: () => apiClient.get('/schema/proPatientsCReportSchema'), 
  modelProPatientsCReport: (id1, id2) => apiClient.get(`/report/${id1}/${id2}`),
  createProPatientsCReport: (id1, id2, id3, sectionTextChange, config = {}) => apiClient.post(`/report/${id1}/${id2}/${id3}`, { sectionTextChange }, { ...config }),
  updateProPatientsCReport: (id, model) => apiClient.put(`/report/${id}`, model),
  
  // AnamnesisIdent
  dataPatSchema: () => apiClient.get('/schema/dataPatSchema'),
  getModelIdent: (id) => apiClient.get(`/identAbstract/${id}`),
  modelIdent: (id, model) =>
    apiClient.post(`/identAbstract/${id}`, model),

  // AnamnesisIndex
  indexSchema: () => apiClient.get('/schema/indexSchema'),
  anamnesisRoot: (id) => apiClient.get(`/anamnesis/${id}`),

  // AnamnesisPermissions
  getPermissionsIndexHCP: (id1, id2) => apiClient.get(`/permissionsAnamnesis/${id1}/${id2}`),
  getPermissionsHCP: (id1, id2, id3) => apiClient.get(`/permissionsHCPVerif/${id1}/${id2}/${id3}`),
  getObserverHCP: (id1, id2, id3) => apiClient.get(`/permissionsHCPObserver/${id1}/${id2}/${id3}`),

  // FiliationPersonal
  filiationPersonalSchema: () => apiClient.get('/schema/filiationPersonalSchema'),
  catOccupation: () => apiClient.get('/occupation'),
  catMaritalStatus: () => apiClient.get('/maritalStatus'),
  catLevelDescription: (id) => apiClient.get(`/levelDescription/${id}`),
  catReligion: () => apiClient.get('/religion'),
  catInstitutions: () => apiClient.get('/institutions'),
  catHealthServices: () => apiClient.get('/healthServices'),
  catInstitutionReferral: () => apiClient.get('/institutionReferral'),
  catHealthServiceReferral: () => apiClient.get('/healthServiceReferral'),
  modelFiliationPersonal: (id1, id2) => apiClient.get(`/filiation/${id1}/${id2}`),
  createFiliationPersonal: (model) =>
    apiClient.post(`/filiation`, model),
  updateFiliationPersonal: (id, model) =>
    apiClient.put(`/filiation/${id}`, model),
  deleteFiliationPersonal: (id1, id2, id3) => apiClient.delete(`/filiation/${id1}/${id2}/${id3}`),


  // ReasonConsultation
  reasonConsultationSchema: () => apiClient.get('/schema/reasonConsultationSchema'),
  modelReasonConsultation: (id1, id2) =>
    apiClient.get(`/reasonConsultation/${id1}/${id2}`),
  saveReasonConsultation: (model) =>
    apiClient.post(`/reasonConsultation`, model),

  // CAxInterview
  interviewSchema: () => apiClient.get('/schema/interviewSchema'),
  catIntervAccessibility: () => apiClient.get('/intervAccessibility'),
  catIntervAttitude: () => apiClient.get('/intervAttitude'),
  catIntervEyeContact: () => apiClient.get('/intervEyeContact'),
  dataActualCondition: (id) => apiClient.get(`/actualCondition/${id}`),
  modelCAxInterview: (id1, id2) => apiClient.get(`/intervSession/${id1}/${id2}`),
  createCAxInterview: (model) =>
    apiClient.post(`/intervSession`, model),
  updateCAxInterview: (id, model) =>
    apiClient.put(`/intervSession/${id}`, model),
  deleteCAxInterview: (id1, id2, id3) =>
    apiClient.delete(`/intervSession/${id1}/${id2}/${id3}`),

  // CAxAFSMxPhysicalCond
  caxAFSMxPhysicalCondSchema: () => apiClient.get('/schema/caxAFSMxPhysicalCondSchema'),
  catPhysicComplexion: () => apiClient.get('/physicComplexion'),
  catPhysicVestment: () => apiClient.get('/physicVestment'),
  catPhysicCleanliness: () => apiClient.get('/physicCleanliness'),
  catPhysicVestmentColor: () => apiClient.get('/physicVestmentColor'),
  modelCAxAFSMxPhysicalCond: (id1, id2) =>
    apiClient.get(`/physicalCondition/${id1}/${id2}`),
  createCAxAFSMxPhysicalCond: (model) =>
    apiClient.post(`/physicalCondition`, model),
  updateCAxAFSMxPhysicalCond: (id, model) =>
    apiClient.put(`/physicalCondition/${id}`, model),

  // CAxAFSMxDysfunction
  caxAFSMxDysfunctionSchema: () => apiClient.get('/schema/caxAFSMxDysfunctionSchema'),
  catBody: () => apiClient.get('/body'),
  catLaterality: () => apiClient.get('/laterality'),
  catSensory: () => apiClient.get('/sensory'),
  catPhysicCondBody: () => apiClient.get('/physicCondBody'),
  catPhysicCondSensory: () => apiClient.get('/physicCondSensory'),
  modelCAxAFSMxDysfunction: (id1, id2) =>
    apiClient.get(`/physicalDysfunction/${id1}/${id2}`),
  createCAxAFSMxDysfunction: (model) =>
    apiClient.post(`/physicalDysfunction`, model),
  updateCAxAFSMxDysfunction: (id, model) =>
    apiClient.put(`/physicalDysfunction/${id}`, model),
  deleteCAxAFSMxDysfunction: (id1, id2, id3) =>
    apiClient.delete(`/physicalDysfunction/${id1}/${id2}/${id3}`),

  // CAxAFSMxPsychomotor
  caxAFSMxPsychomotorSchema: () => apiClient.get('/schema/caxAFSMxPsychomotorSchema'),
  catPhysicPsychomotForwardBack: () => apiClient.get('/physicPsychomotForwardBack'),
  catPhysicPsychomotRightLeft: () => apiClient.get('/physicPsychomotRightLeft'),
  catPhysicPsychomotDisplaCondi: () => apiClient.get('/physicPsychomotDisplaCondi'),
  catPhysicPsychomotMuscleCondi: () => apiClient.get('/physicPsychomotMuscleCondi'),
  catPhysicPsychomotKinetCondi: () => apiClient.get('/physicPsychomotKinetCondi'),
  catPhysicPsychomotSkillsType: () => apiClient.get('/physicPsychomotSkillsType'),
  modelCAxAFSMxPsychomotor: (id1, id2) =>
    apiClient.get(`/physicalPsychomotricity/${id1}/${id2}`),
  createCAxAFSMxPsychomotor: (model) =>
    apiClient.post(`/physicalPsychomotricity`, model),
  updateCAxAFSMxPsychomotor: (id, model) =>
    apiClient.put(`/physicalPsychomotricity/${id}`, model),
  deleteCAxAFSMxPsychomotor: (id) =>
    apiClient.delete(`/physicalPsychomotricity/${id}`),

  // CAxAFSMxClarifications
  caxAFSMxClarificationsSchema: () => apiClient.get('/schema/caxAFSMxClarificationsSchema'),
  modelCAxAFSMxClarifications: (id1, id2) =>
    apiClient.get(`/physicalAspectClarifications/${id1}/${id2}`),
  saveCAxAFSMxClarifications: (model) =>
    apiClient.post(`/physicalAspectClarifications`, model),

  // CAxAFFxBasicHabits
  caxAFFxBasicHabitsSchema: () => apiClient.get('/schema/caxAFFxBasicHabitsSchema'),
  modelCAxAFFxBasicHabits: (id1, id2) =>
    apiClient.get(`/physiolBasicHabits/${id1}/${id2}`),
  createCAxAFFxBasicHabits: (model) =>
    apiClient.post(`/physiolBasicHabits`, model),
  updateCAxAFFxBasicHabits: (id, model) =>
    apiClient.put(`/physiolBasicHabits/${id}`, model),

  // CAxAFFxPhysiolSymptoms
  caxAFFxPhysiolSymptomsSchema: () => apiClient.get('/schema/caxAFFxPhysiolSymptomsSchema'),
  modelCAxAFFxPhysiolSymptoms: (id1, id2) =>
    apiClient.get(`/physiolSymptoms/${id1}/${id2}`),
  createCAxAFFxPhysiolSymptoms: (model) =>
    apiClient.post(`/physiolSymptoms`, model),
  updateCAxAFFxPhysiolSymptoms: (id, model) =>
    apiClient.put(`/physiolSymptoms/${id}`, model),

  // CAxAFFxMuscularSymptoms
  caxAFFxMuscularSymptomsSchema: () => apiClient.get('/schema/caxAFFxMuscularSymptomsSchema'),
  catPhysiolMuscle: () => apiClient.get('/physiolMuscle'),
  catPhysiolMuscleLateraly: () => apiClient.get('/physiolMuscleLateraly'),
  catPhysiolMusclePart: () => apiClient.get('/physiolMusclePart'),
  modelCAxAFFxMuscularSymptoms: (id1, id2) =>
    apiClient.get(`/physiolMuscular/${id1}/${id2}`),
  createCAxAFFxMuscularSymptoms: (model) =>
    apiClient.post(`/physiolMuscular`, model),
  updateCAxAFFxMuscularSymptoms: (id, model) =>
    apiClient.put(`/physiolMuscular/${id}`, model),
  deleteCAxAFFxMuscularSymptoms: (id1, id2, id3) =>
    apiClient.delete(`/physiolMuscular/${id1}/${id2}/${id3}`),

  // CAxAFFxClarifications
  caxAFFxClarificationsSchema: () => apiClient.get('/schema/caxAFFxClarificationsSchema'),
  modelCAxAFFxClarifications: (id1, id2) =>
    apiClient.get(`/physiologicalAspectClarifications/${id1}/${id2}`),
  saveCAxAFFxClarifications: (model) =>
    apiClient.post(`/physiologicalAspectClarifications`, model),

  // CAxACGxAttention
  caxACGxAttentionSchema: () => apiClient.get('/schema/caxACGxAttentionSchema'),
  catCognitAwareness: () => apiClient.get('/cognitAwareness'),
  catLevelProof: () => apiClient.get('/levelProof'),
  catCognitAttentionCond: () => apiClient.get('/cognitAttentionCond'),
  modelCAxACGxAttention: (id1, id2) =>
    apiClient.get(`/cognitiveAttention/${id1}/${id2}`),
  createCAxACGxAttention: (model) =>
    apiClient.post(`/cognitiveAttention`, model),
  updateCAxACGxAttention: (id, model) =>
    apiClient.put(`/cognitiveAttention/${id}`, model),

  // CAxACGxPerception
  caxACGxPerceptionSchema: () => apiClient.get('/schema/caxACGxPerceptionSchema'),
  catCognitSense: () => apiClient.get('/cognitSense'),
  catCognitSenseCond: () => apiClient.get('/cognitSenseCond'),
  catCognitPercepCond: () => apiClient.get('/cognitPercepCond'),
  modelCAxACGxPerception: (id1, id2) =>
    apiClient.get(`/cognitivePerception/${id1}/${id2}`),
  createCAxACGxPerception: (model) =>
    apiClient.post(`/cognitivePerception`, model),
  updateCAxACGxPerception: (id, model) =>
    apiClient.put(`/cognitivePerception/${id}`, model),

  // CAxACGxMemory
  caxACGxMemorySchema: () => apiClient.get('/schema/caxACGxMemorySchema'),
  catCognitMemoryCond: () => apiClient.get('/cognitMemoryCond'),
  catCognitMemoryAmnesiaOrigin: () => apiClient.get('/cognitMemoryAmnesiaOrigin'),
  catCognitMemoryAmnesiaType: () => apiClient.get('/cognitMemoryAmnesiaType'),
  catCognitMemoryAmnesiaEpisode: () => apiClient.get('/cognitMemoryAmnesiaEpisode'),
  modelCAxACGxMemory: (id1, id2) => apiClient.get(`/cognitiveMemory/${id1}/${id2}`),
  createCAxACGxMemory: (model) =>
    apiClient.post(`/cognitiveMemory`, model),
  updateCAxACGxMemory: (id, model) =>
    apiClient.put(`/cognitiveMemory/${id}`, model),
  deleteCAxACGxMemory:  (id1, id2, id3) =>
    apiClient.delete(`/cognitiveMemory/${id1}/${id2}/${id3}`),

  // CAxACGxThoughtPt1
  caxACGxThoughtPt1Schema: () => apiClient.get('/schema/caxACGxThoughtPt1Schema'),
  catCognitThouTrainFormCond: () => apiClient.get('/cognitThouTrainFormCond'),
  modelCAxACGxThoughtPt1: (id1, id2) =>
    apiClient.get(`/cognitThoughtTrain/${id1}/${id2}`),
  createCAxACGxThoughtPt1: (model) =>
    apiClient.post(`/cognitThoughtTrain`, model),
  updateCAxACGxThoughtPt1: (id, model) =>
    apiClient.put(`/cognitThoughtTrain/${id}`, model),
  deleteCAxACGxThoughtPt1: (id) =>
    apiClient.delete(`/cognitThoughtTrain/${id}`),

  // CAxACGxThoughtPt2
  caxACGxThoughtPt2Schema: () => apiClient.get('/schema/caxACGxThoughtPt2Schema'),
  modelCAxACGxThoughtPt2: (id1, id2) =>
    apiClient.get(`/cognitThoughtContentA/${id1}/${id2}`),
  createCAxACGxThoughtPt2: (model) =>
    apiClient.post(`/cognitThoughtContentA`, model),
  updateCAxACGxThoughtPt2: (id, model) =>
    apiClient.put(`/cognitThoughtContentA/${id}`, model),

  // CAxACGxThoughtPt3
  caxACGxThoughtPt3Schema: () => apiClient.get('/schema/caxACGxThoughtPt3Schema'),
  modelCAxACGxThoughtPt3: (id1, id2) =>
    apiClient.get(`/cognitThoughtContentB/${id1}/${id2}`),
  createCAxACGxThoughtPt3: (model) =>
    apiClient.post(`/cognitThoughtContentB`, model),
  updateCAxACGxThoughtPt3: (id, model) =>
    apiClient.put(`/cognitThoughtContentB/${id}`, model),

  // CAxACGxLanguage
  caxACGxLanguageSchema: () => apiClient.get('/schema/caxACGxLanguageSchema'),
  catCognitLangDimCond: () => apiClient.get('/cognitLangDimCond'),
  catCognitLangCond: () => apiClient.get('/cognitLangCond'),
  catCognitIntelligence: () => apiClient.get('/cognitIntelligence'),
  modelCAxACGxLanguage: (id1, id2) =>
    apiClient.get(`/cognitiveLanguage/${id1}/${id2}`),
  createCAxACGxLanguage: (model) =>
    apiClient.post(`/cognitiveLanguage`, model),
  updateCAxACGxLanguage: (id, model) =>
    apiClient.put(`/cognitiveLanguage/${id}`, model),

  // CAxACGxClarifications
  caxACGxClarificationsSchema: () => apiClient.get('/schema/caxACGxClarificationsSchema'),
  modelCAxACGxClarifications: (id1, id2) =>
    apiClient.get(`/cognitiveAspectClarifications/${id1}/${id2}`),
  saveCAxACGxClarifications: (model) =>
    apiClient.post(`/cognitiveAspectClarifications`, model),

  // CAxARPxPersonality
  caxARPxPersonalitySchema: () => apiClient.get('/schema/caxARPxPersonalitySchema'),
  modelCAxARPxPersonality: (id1, id2) =>
    apiClient.get(`/personalityAspect/${id1}/${id2}`),
  createCAxARPxPersonality: (model) =>
    apiClient.post(`/personalityAspect`, model),
  updateCAxARPxPersonality: (id, model) =>
    apiClient.put(`/personalityAspect/${id}`, model),

  // CAxARPxClarifications
  caxARPxClarificationsSchema: () => apiClient.get('/schema/caxARPxClarificationsSchema'),
  modelCAxARPxClarifications: (id1, id2) =>
    apiClient.get(`/personalityAspectClarifications/${id1}/${id2}`),
  saveCAxARPxClarifications: (model) =>
    apiClient.post(`/personalityAspectClarifications`, model),

  // CAxAAMxAffectivity
  caxAAMxAffectivitySchema: () => apiClient.get('/schema/caxAAMxAffectivitySchema'),
  catAffectiveReaction: () => apiClient.get('/affectivReaction'),
  modelCAxAAMxAffectivity: (id1, id2) =>
    apiClient.get(`/affectivityAspect/${id1}/${id2}`),
  createCAxAAMxAffectivity: (model) =>
    apiClient.post(`/affectivityAspect`, model),
  updateCAxAAMxAffectivity: (id, model) =>
    apiClient.put(`/affectivityAspect/${id}`, model),

  // CAxAAMxClarifications
  caxAAMxClarificationsSchema: () => apiClient.get('/schema/caxAAMxClarificationsSchema'),
  modelCAxAAMxClarifications: (id1, id2) =>
    apiClient.get(`/affectivityAspectClarifications/${id1}/${id2}`),
  saveCAxAAMxClarifications: (model) =>
    apiClient.post(`/affectivityAspectClarifications`, model),

  // CAxACOxBehavioral
  caxACOxBehavioralSchema: () => apiClient.get('/schema/caxACOxBehavioralSchema'),
  modelCAxACOxBehavioral: (id1, id2) =>
    apiClient.get(`/behavioralAspect/${id1}/${id2}`),
  createCAxACOxBehavioral: (model) =>
    apiClient.post(`/behavioralAspect`, model),
  updateCAxACOxBehavioral: (id, model) =>
    apiClient.put(`/behavioralAspect/${id}`, model),

  // CAxACOxClarifications
  caxACOxClarificationsSchema: () => apiClient.get('/schema/caxACOxClarificationsSchema'),
  modelCAxACOxClarifications: (id1, id2) =>
    apiClient.get(`/behavioralAspectClarifications/${id1}/${id2}`),
  saveCAxACOxClarifications: (model) =>
    apiClient.post(`/behavioralAspectClarifications`, model),

  // CAxAFAxFamiliarPt1
  caxAFAxFamiliarPt1Schema: () => apiClient.get('/schema/caxAFAxFamiliarPt1Schema'),
  modelCAxAFAxFamiliarPt1: (id1, id2) => apiClient.get(`/familyA/${id1}/${id2}`),
  createCAxAFAxFamiliarPt1: (model) =>
    apiClient.post(`/familyA`, model),
  updateCAxAFAxFamiliarPt1: (id, model) =>
    apiClient.put(`/familyA/${id}`, model),
  deleteCAxAFAxFamiliarPt1: (id1, id2, id3) =>
    apiClient.delete(`/familyA/${id1}/${id2}/${id3}`),

  // CAxAFAxFamiliarPt2
  caxAFAxFamiliarPt2Schema: () => apiClient.get('/schema/caxAFAxFamiliarPt2Schema'),
  catFamDim: (id) => apiClient.get(`/famDim/${id}`),
  modelCAxAFAxFamiliarPt2: (id1, id2) => apiClient.get(`/familyB/${id1}/${id2}`),
  createCAxAFAxFamiliarPt2: (model) =>
    apiClient.post(`/familyB`, model),
  updateCAxAFAxFamiliarPt2: (id, model) =>
    apiClient.put(`/familyB/${id}`, model),
  deleteCAxAFAxFamiliarPt2: (id1, id2, id3) =>
    apiClient.delete(`/familyB/${id1}/${id2}/${id3}`),

  // CAxAFAxFamiliarPt3
  caxAFAxFamiliarPt3Schema: () => apiClient.get('/schema/caxAFAxFamiliarPt3Schema'),
  modelCAxAFAxFamiliarPt3: (id1, id2) => apiClient.get(`/familyC/${id1}/${id2}`),
  createCAxAFAxFamiliarPt3: (model) =>
    apiClient.post(`/familyC`, model),
  updateCAxAFAxFamiliarPt3: (id, model) =>
    apiClient.put(`/familyC/${id}`, model),
  deleteCAxAFAxFamiliarPt3: (id1, id2, id3) =>
    apiClient.delete(`/familyC/${id1}/${id2}/${id3}`),

  // CAxAFAxClarifications
  caxAFAxClarificationsSchema: () => apiClient.get('/schema/caxAFAxClarificationsSchema'),
  modelCAxAFAxClarifications: (id1, id2) =>
    apiClient.get(`/familyAspectClarifications/${id1}/${id2}`),
  saveCAxAFAxClarifications: (model) =>
    apiClient.post(`/familyAspectClarifications`, model),

  // CAxASCxSocialPt1
  caxASCxSocialPt1Schema: () => apiClient.get('/schema/caxASCxSocialPt1Schema'),
  catSocialInteraction: () => apiClient.get('/socInteraction'),
  modelCAxASCxSocialPt1: (id1, id2) => apiClient.get(`/socialA/${id1}/${id2}`),
  createCAxASCxSocialPt1: (model) =>
    apiClient.post(`/socialA`, model),
  updateCAxASCxSocialPt1: (id, model) =>
    apiClient.put(`/socialA/${id}`, model),

  // CAxASCxSocialPt2
  caxASCxSocialPt2Schema: () => apiClient.get('/schema/caxASCxSocialPt2Schema'),
  modelCAxASCxSocialPt2: (id1, id2) => apiClient.get(`/socialB/${id1}/${id2}`),
  createCAxASCxSocialPt2: (model) =>
    apiClient.post(`/socialB`, model),
  updateCAxASCxSocialPt2: (id, model) =>
    apiClient.put(`/socialB/${id}`, model),

  // CAxASCxSocialPt3
  caxASCxSocialPt3Schema: () => apiClient.get('/schema/caxASCxSocialPt3Schema'),
  catPsychoSubstance: () => apiClient.get('/psychoSubstance'),
  catFrequency: () => apiClient.get('/frequency'),
  catSubstanceHabit: () => apiClient.get('/substanceHabit'),
  modelCAxASCxSocialPt3: (id1, id2) => apiClient.get(`/socialC/${id1}/${id2}`),
  createCAxASCxSocialPt3: (model) =>
    apiClient.post(`/socialC`, model),
  updateCAxASCxSocialPt3: (id, model) =>
    apiClient.put(`/socialC/${id}`, model),
  deleteCAxASCxSocialPt3: (id1, id2, id3) => apiClient.delete(`/socialC/${id1}/${id2}/${id3}`),

  // CAxASCxSocialPt4
  caxASCxSocialPt4Schema: () => apiClient.get('/schema/caxASCxSocialPt4Schema'),
  catGenders: () => apiClient.get('/genders'),
  catDuration: () => apiClient.get('/duration'),
  catCoupleBond: () => apiClient.get('/coupleBond'),
  modelCAxASCxSocialPt4: (id1, id2) => apiClient.get(`/socialD/${id1}/${id2}`),
  createCAxASCxSocialPt4: (model) =>
    apiClient.post(`/socialD`, model),
  updateCAxASCxSocialPt4: (id, model) =>
    apiClient.put(`/socialD/${id}`, model),

  // CAxASCxSocialPt5
  caxASCxSocialPt5Schema: () => apiClient.get('/schema/caxASCxSocialPt5Schema'),
  modelCAxASCxSocialPt5: (id1, id2) => apiClient.get(`/socialE/${id1}/${id2}`),
  createCAxASCxSocialPt5: (model) =>
    apiClient.post(`/socialE`, model),
  updateCAxASCxSocialPt5: (id, model) =>
    apiClient.put(`/socialE/${id}`, model),
  deleteCAxASCxSocialPt5: (id1, id2, id3) => apiClient.delete(`/socialE/${id1}/${id2}/${id3}`),

  // CAxASCxSocialPt6
  caxASCxSocialPt6Schema: () => apiClient.get('/schema/caxASCxSocialPt6Schema'),
  modelCAxASCxSocialPt6: (id1, id2) => apiClient.get(`/socialF/${id1}/${id2}`),
  createCAxASCxSocialPt6: (model) =>
    apiClient.post(`/socialF`, model),
  updateCAxASCxSocialPt6: (id, model) =>
    apiClient.put(`/socialF/${id}`, model),
  deleteCAxASCxSocialPt6: (id1, id2, id3) => apiClient.delete(`/socialF/${id1}/${id2}/${id3}`),

  // CAxASCxClarifications
  caxASCxClarificationsSchema: () => apiClient.get('/schema/caxASCxClarificationsSchema'),
  modelCAxASCxClarifications: (id1, id2) =>
    apiClient.get(`/socialAspectClarifications/${id1}/${id2}`),
  saveCAxASCxClarifications: (model) =>
    apiClient.post(`/socialAspectClarifications`, model),

  // CAxASXxSexual
  caxASXxSexualSchema: () => apiClient.get('/schema/caxASXxSexualSchema'),
  catCurrentSexes: () => apiClient.get('/currentSexes'),
  catSexOrientation: () => apiClient.get('/sexOrientation'),
  catSexRelationship: () => apiClient.get('/sexRelationship'),
  catSexPhases: () => apiClient.get('/sexPhases'),
  catSexDysfunction: () => apiClient.get('/sexDysfunction'),
  catSexDysfAcquisition: () => apiClient.get('/sexDysfAcquisition'),
  catSexDysfSituation: () => apiClient.get('/sexDysfSituation'),
  catSexCondition: () => apiClient.get('/sexCondition'),
  catSexParaphilias: () => apiClient.get('/sexParaphilias'),
  catSexParaphiliasSpec: () => apiClient.get('/sexParaphiliasSpec'),
  catSexContraceptive: () => apiClient.get('/sexContraceptive'),
  modelCAxASXxSexual: (id1, id2) => apiClient.get(`/sexualAspect/${id1}/${id2}`),
  createCAxASXxSexual: (model) =>
    apiClient.post(`/sexualAspect`, model),
  updateCAxASXxSexual: (id, model) =>
    apiClient.put(`/sexualAspect/${id}`, model),

  // CAxASXxClarifications
  caxASXxClarificationsSchema: () => apiClient.get('/schema/caxASXxClarificationsSchema'),
  modelCAxASXxClarifications: (id1, id2) =>
    apiClient.get(`/sexualAspectClarifications/${id1}/${id2}`),
  saveCAxASXxClarifications: (model) =>
    apiClient.post(`/sexualAspectClarifications`, model),

  // CAxAALxAcademic
  caxAALxAcademicSchema: () => apiClient.get('/schema/caxAALxAcademicSchema'),
  catInstitutionType: () => apiClient.get('/acadLaborInstitutionType'),
  catYear: () => apiClient.get('/year'),
  modelCAxAALxAcademic: (id1, id2) => apiClient.get(`/academic/${id1}/${id2}`),
  createCAxAALxAcademic: (model) =>
    apiClient.post(`/academic`, model),
  updateCAxAALxAcademic: (id, model) =>
    apiClient.put(`/academic/${id}`, model),
  deleteCAxAALxAcademic: (id1, id2, id3) => apiClient.delete(`/academic/${id1}/${id2}/${id3}`),

  // CAxAALxLabor
  caxAALxLaborSchema: () => apiClient.get('/schema/caxAALxLaborSchema'),
  modelCAxAALxLabor: (id1, id2) => apiClient.get(`/labor/${id1}/${id2}`),
  createCAxAALxLabor: (model) => apiClient.post(`/labor`, model),
  updateCAxAALxLabor: (id, model) =>
    apiClient.put(`/labor/${id}`, model),
  deleteCAxAALxLabor: (id1, id2, id3) => apiClient.delete(`/labor/${id1}/${id2}/${id3}`),

  // CAxAALxClarifications
  caxAALxClarificationsSchema: () => apiClient.get('/schema/caxAALxClarificationsSchema'),
  modelCAxAALxClarifications: (id1, id2) =>
    apiClient.get(`/academicLaborAspectClarifications/${id1}/${id2}`),
  saveCAxAALxClarifications: (model) =>
    apiClient.post(`/academicLaborAspectClarifications`, model),

  // CAxCExSpecificCond
  caxCExSpecificCondSchema: () => apiClient.get('/schema/caxCExSpecificCondSchema'),
  catSpecCondPsycDisorder: () => apiClient.get('/specCondPsycDisorder'),
  catSpecCondDepreDisorder: () => apiClient.get('/specCondDepreDisorder'),
  catSpecCondDeliriumDisorder: () => apiClient.get('/specCondDeliriumDisorder'),
  catSpecCondAffecOtherDisease: () => apiClient.get('/specCondAffecOtherDisease'),
  modelCAxCExSpecificCond: (id1, id2) =>
    apiClient.get(`/specificConditions/${id1}/${id2}`),
  createCAxCExSpecificCond: (model) =>
    apiClient.post(`/specificConditions`, model),
  updateCAxCExSpecificCond: (id, model) =>
    apiClient.put(`/specificConditions/${id}`, model),

  // CAxCExClarifications
  caxCExClarificationsSchema: () => apiClient.get('/schema/caxCExClarificationsSchema'),
  modelCAxCExClarifications: (id1, id2) =>
    apiClient.get(`/specConditionClarifications/${id1}/${id2}`),
  saveCAxCExClarifications: (model) =>
    apiClient.post(`/specConditionClarifications`, model),

  // ANxADxInfancy
  anxADxInfancySchema: () => apiClient.get('/schema/anxADxInfancySchema'),
  catDevLactation: () => apiClient.get('/devLactation'),
  modelANxADxInfancy: (id1, id2) => apiClient.get(`/backDevInfancy/${id1}/${id2}`),
  createANxADxInfancy: (model) =>
    apiClient.post(`/backDevInfancy`, model),
  updateANxADxInfancy: (id, model) =>
    apiClient.put(`/backDevInfancy/${id}`, model),

  // ANxADxChildhood
  anxADxChildhoodSchema: () => apiClient.get('/schema/anxADxChildhoodSchema'),
  catAttachment: () => apiClient.get('/attachment'),
  catAgeInterval: () => apiClient.get('/ageInterval'),
  catBond: () => apiClient.get('/bond'),
  catCurrentSexGender: () => apiClient.get('/currentSexGender'),
  modelANxADxChildhood: (id1, id2) =>
    apiClient.get(`/backDevChildhood/${id1}/${id2}`),
  createANxADxChildhood: (model) =>
    apiClient.post(`/backDevChildhood`, model),
  updateANxADxChildhood: (id, model) =>
    apiClient.put(`/backDevChildhood/${id}`, model),

  // ANxADxAdolescence
  anxADxAdolescenceSchema: () => apiClient.get('/schema/anxADxAdolescenceSchema'),
  modelANxADxAdolescence: (id1, id2) =>
    apiClient.get(`/backDevAdolescence/${id1}/${id2}`),
  createANxADxAdolescence: (model) =>
    apiClient.post(`/backDevAdolescence`, model),
  updateANxADxAdolescence: (id, model) =>
    apiClient.put(`/backDevAdolescence/${id}`, model),

  // ANxADxSpecific
  anxADxSpecificSchema: () => apiClient.get('/schema/anxADxSpecificSchema'),
  catSpecDiseases: () => apiClient.get('/devSpecDiseases'),
  catSpecAllergies: () => apiClient.get('/devSpecAllergies'),
  catSexualAssault: () => apiClient.get('/devSpecSexualAssault'),
  modelANxADxSpecific: (id1, id2) =>
    apiClient.get(`/backDevSpecific/${id1}/${id2}`),
  createANxADxSpecific: (model) =>
    apiClient.post(`/backDevSpecific`, model),
  updateANxADxSpecific: (id, model) =>
    apiClient.put(`/backDevSpecific/${id}`, model),
  deleteANxADxSpecific: (id1, id2, id3) =>
    apiClient.delete(`/backDevSpecific/${id1}/${id2}/${id3}`),
    
  // ANxAFxFamilyMembers
  anxAFxFamilyMembersSchema: () => apiClient.get('/schema/anxAFxFamilyMembersSchema'),
  catDevelopmentStages: () => apiClient.get('/developmentStages'),
  catDeathCauses: () => apiClient.get('/deathCauses'),
  catDiagnosticManual: () => apiClient.get('/diagnosticManual'),
  catDiagnosticICD10: () => apiClient.get('/diagnosticICD10'),
  catDiagnosticICD11: () => apiClient.get('/diagnosticICD11'),
  catDiagnosticDSM5: () => apiClient.get('/diagnosticDSM5'),
  catPersonTrait: () => apiClient.get('/personTrait'),
  catCoupleFamiliarity: () => apiClient.get('/coupleFamiliarity'),
  dataCrudANxAFxFamilyMembers: (id) => apiClient.get(`/backFamMember/${id}`),
  modelANxAFxFamilyMembers: (id1, id2) =>
    apiClient.get(`/backFamMember/${id1}/${id2}`),
  createANxAFxFamilyMembers: (model) =>
    apiClient.post(`/backFamMember`, model),
  updateANxAFxFamilyMembers: (id, model) =>
    apiClient.put(`/backFamMember/${id}`, model),
  deleteANxAFxFamilyMembers: (id1, id2, id3) =>
    apiClient.delete(`/backFamMember/${id1}/${id2}/${id3}`),

  // ANxALxAcademBasic
  anxALxAcademBasicSchema: () => apiClient.get('/schema/anxALxAcademBasicSchema'),
  catInstructionGrade: () => apiClient.get('/instructionGrade'),
  catSchoolType: () => apiClient.get('/schoolType'),
  catCourses: () => apiClient.get('/courses'),
  dataCrudANxALxAcademBasic: (id) => apiClient.get(`/backAcademBasic/${id}`),
  modelANxALxAcademBasic: (id1, id2) =>
    apiClient.get(`/backAcademBasic/${id1}/${id2}`),
  createANxALxAcademBasic: (model) =>
    apiClient.post(`/backAcademBasic`, model),
  updateANxALxAcademBasic: (id, model) =>
    apiClient.put(`/backAcademBasic/${id}`, model),
  deleteANxALxAcademBasic: (id1, id2, id3) =>
    apiClient.delete(`/backAcademBasic/${id1}/${id2}/${id3}`),
 
  // ANxALxAcademSuperior
  anxALxAcademSuperiorSchema: () => apiClient.get('/schema/anxALxAcademSuperiorSchema'),
  catCareerCondition: () => apiClient.get('/careerCondition'),
  catLaborType: () => apiClient.get('/laborType'),
  catLaborLeave: () => apiClient.get('/laborLeave'),
  catLaborLeaveWhy: () => apiClient.get('/laborLeaveWhy'),
  modelANxALxAcademSuperior: (id1, id2) =>
    apiClient.get(`/backAcademSuperior/${id1}/${id2}`),
  createANxALxAcademSuperior: (model) =>
    apiClient.post(`/backAcademSuperior`, model),
  updateANxALxAcademSuperior: (id, model) =>
    apiClient.put(`/backAcademSuperior/${id}`, model),
  deleteANxALxAcademSuperior: (id1, id2, id3) =>
    apiClient.delete(`/backAcademSuperior/${id1}/${id2}/${id3}`),

  // ANxALxLabor
  anxALxLaborSchema: () => apiClient.get('/schema/anxALxLaborSchema'),
  modelANxALxLabor: (id1, id2) => apiClient.get(`/backLabor/${id1}/${id2}`),
  createANxALxLabor: (model) => apiClient.post(`/backLabor`, model),
  updateANxALxLabor: (id, model) =>
    apiClient.put(`/backLabor/${id}`, model),
  deleteANxALxLabor: (id1, id2, id3) => apiClient.delete(`/backLabor/${id1}/${id2}/${id3}`),

  // ANxAPxMental
  anxAPxMentalSchema: () => apiClient.get('/schema/anxAPxMentalSchema'),
  catProblemTerapyType: () => apiClient.get('/problemTerapyType'),
  modelANxAPxMental: (id1, id2) =>
    apiClient.get(`/backProblemMental/${id1}/${id2}`),
  createANxAPxMental: (model) =>
    apiClient.post(`/backProblemMental`, model),
  updateANxAPxMental: (id, model) =>
    apiClient.put(`/backProblemMental/${id}`, model),
  deleteANxAPxMental: (id1, id2, id3) =>
    apiClient.delete(`/backProblemMental/${id1}/${id2}/${id3}`),
   

  // ANxAPxMedical
  anxAPxMedicalSchema: () => apiClient.get('/schema/anxAPxMedicalSchema'),
  modelANxAPxMedical: (id1, id2) =>
    apiClient.get(`/backProblemMedical/${id1}/${id2}`),
  createANxAPxMedical: (model) =>
    apiClient.post(`/backProblemMedical`, model),
  updateANxAPxMedical: (id, model) =>
    apiClient.put(`/backProblemMedical/${id}`, model),
  deleteANxAPxMedical: (id1, id2, id3) =>
    apiClient.delete(`/backProblemMedical/${id1}/${id2}/${id3}`),

  // ANxAPxDrugs
  anxAPxDrugsSchema: () => apiClient.get('/schema/anxAPxDrugsSchema'),
  modelANxAPxDrugs: (id1, id2) => apiClient.get(`/backProblemDrugs/${id1}/${id2}`),
  createANxAPxDrugs: (model) =>
    apiClient.post(`/backProblemDrugs`, model),
  updateANxAPxDrugs: (id, model) =>
    apiClient.put(`/backProblemDrugs/${id}`, model),
  deleteANxAPxDrugs: (id1, id2, id3) =>
    apiClient.delete(`/backProblemDrugs/${id1}/${id2}/${id3}`),

  // ANxAVxViolence
  anxAVxViolenceSchema: () => apiClient.get('/schema/anxAVxViolenceSchema'),
  catViolenceType: () => apiClient.get('/violenceType'),
  modelANxAVxViolence: (id1, id2) => apiClient.get(`/backViolence/${id1}/${id2}`),
  createANxAVxViolence: (model) =>
    apiClient.post(`/backViolence`, model),
  updateANxAVxViolence: (id, model) =>
    apiClient.put(`/backViolence/${id}`, model),
  deleteANxAVxViolence: (id1, id2, id3) =>
    apiClient.delete(`/backViolence/${id1}/${id2}/${id3}`),
    
  // ANxACxCriminal
  anxACxCriminalSchema: () => apiClient.get('/schema/anxACxCriminalSchema'),
  catCrime: () => apiClient.get('/crime'),
  catCrimeType: () => apiClient.get('/crimeType'),
  catCrimeWeapon: () => apiClient.get('/crimeWeapon'),
  catCrimeIntervention: () => apiClient.get('/crimeIntervention'),
  modelANxACxCriminal: (id1, id2) => apiClient.get(`/backCriminal/${id1}/${id2}`),
  createANxACxCriminal: (model) =>
    apiClient.post(`/backCriminal`, model),
  updateANxACxCriminal: (id, model) =>
    apiClient.put(`/backCriminal/${id}`, model),
  deleteANxACxCriminal: (id1, id2, id3) =>
    apiClient.delete(`/backCriminal/${id1}/${id2}/${id3}`),
  
  // ANxMExMedications
  anxMExMedicationSchema: () => apiClient.get('/schema/anxMExMedicationSchema'),
  catMedicationSection: () => apiClient.get('/medicationSection'),
  catMedication: () => apiClient.get('/medication'),
  catMedicationRoute: () => apiClient.get('/MedicationRoute'),
  modelANxMExMedication: (id1, id2) =>
    apiClient.get(`/backMedication/${id1}/${id2}`),
  createANxMExMedication: (model) =>
    apiClient.post(`/backMedication`, model),
  updateANxMExMedication: (id, model) =>
    apiClient.put(`/backMedication/${id}`, model),
  deleteANxMExMedication: (id1, id2, id3) =>
    apiClient.delete(`/backMedication/${id1}/${id2}/${id3}`),

  // ANxERxRelevantEvents
  anxERxRelevantEventsSchema: () => apiClient.get('/schema/anxERxRelevantEventsSchema'),
  modelANxERxRelevantEvents: (id1, id2) => apiClient.get(`/backRelevantEvents/${id1}/${id2}`),
  createANxERxRelevantEvents: (model) => apiClient.post(`/backRelevantEvents`, model),
  updateANxERxRelevantEvents: (id, model) => apiClient.put(`/backRelevantEvents/${id}`, model),
  deleteANxERxRelevantEvents: (id1, id2, id3) => apiClient.delete(`/backRelevantEvents/${id1}/${id2}/${id3}`),
  
  // ANxClarifications
  anxClarificationsSchema: () => apiClient.get('/schema/anxClarificationsSchema'),
  modelANxClarifications: (id1, id2) =>
    apiClient.get(`/backClarifications/${id1}/${id2}`),
  saveANxClarifications: (model) =>
    apiClient.post(`/backClarifications`, model),

  // DSxDetailsSymptoms
  dsxDetailsSymptomsSchema: () => apiClient.get('/schema/dsxDetailsSymptomsSchema'),
  catDetailsSympVariable: () => apiClient.get('/detailsSympVariable'),
  catDetailsSympCT: () => apiClient.get('/detailsSympCT'),
  catDetailsSympOnset: () => apiClient.get('/detailsSympOnset'),
  catDetailsSympCourse: () => apiClient.get('/detailsSympCourse'),
  catDetailsSympManifestType: () => apiClient.get('/detailsSympManifestType'),
  modelDSxDetailsSymptoms: (id1, id2, id3) => apiClient.get(`/detailsSymptoms/${id1}/${id2}/${id3}`),
  modelTraceDiag: (id1, id2) => apiClient.get(`/traceDiag/${id1}/${id2}`),
  updateDSxDetailsSymptoms: (id, model) => apiClient.put(`/detailsSymptoms/${id}`, model),
  deleteDSxDetailsSymptoms: (id1, id2, id3, id4) => apiClient.delete(`/detailsSymptoms/${id1}/${id2}/${id3}/${id4}`),
  createTraceDiag: (id1, id2, id3) => apiClient.post(`/traceDiag/${id1}/${id2}/${id3}`),
  updateTraceDiag: (id1, id2, id3) => apiClient.put(`/traceDiag/${id1}/${id2}/${id3}`),

  // DTxDiagTreatment
  dtxDiagTreatmentSchema: () => apiClient.get('/schema/dtxDiagTreatmentSchema'),
  catDiagnosticType: () => apiClient.get('/diagnosticType'),
  catTreatmentActivity: () => apiClient.get('/treatmentActivity'),
  catTreatmentSubject: () => apiClient.get('/treatmentSubject'),
  catTreatmentObjective: () => apiClient.get('/treatmentObjective'),
  catTreatmentDerivation: () => apiClient.get('/treatmentDerivation'),
  modelDTxDiagTreatment: (id1, id2) => apiClient.get(`/diagTreatment/${id1}/${id2}`),
  createDTxDiagTreatment: (model) => apiClient.post(`/diagTreatment`, model),
  updateDTxDiagTreatment: (id, model) => apiClient.put(`/diagTreatment/${id}`, model),
  deleteDTxDiagTreatment: (id1, id2, id3, id4) => apiClient.delete(`/diagTreatment/${id1}/${id2}/${id3}/${id4}`),

  // OGxGeneralObservation
  ogxGeneralObservationSchema: () => apiClient.get('/schema/ogxGeneralObservationSchema'),
  modelOGxGeneralObservation: (id1, id2) =>
    apiClient.get(`/generalObservation/${id1}/${id2}`),
  saveOGxGeneralObservation: (model) =>
    apiClient.post(`/generalObservation`, model),

  // TestCOxList
  testCOxListSchema: () => apiClient.get('/schema/testCOxListSchema'),
  dataTestCOxList: (id) => apiClient.get(`/clinicalHistoriesHasCorrector/${id}`),
  dataTests: (id) => apiClient.get(`/testCOxList/data/${id}`),
  downloadTest: (correctorId) => apiClient.get(`/testCOxList/download/${correctorId}`),
  deleteTestCOxList: (id1, coNameNum, id2, id3) => apiClient.delete(`/testCorrector${coNameNum}/${id1}/${id2}/${id3}`),
  deleteTestCO: (model) => apiClient.post(`/testCOxList/delete`, model),

  // TestCorrectors
  testCorrectorsSchema: () => apiClient.get('/schema/testCorrectorsSchema'),
  dataTestCorrectors: (id) => apiClient.get(`/testCorrector/${id}`),
  dataLlaveCo: (id) => apiClient.get(`/llaveCo/${id}`),
  submitLlaveCo: (id, formData) =>
    apiClient3.post(`/llaveCo/${id}`, formData),
  deleteLlaveCo: (id) => apiClient.delete(`/llaveCo/${id}`),

  // TestResults
  testCOxResultsSchema: () => apiClient.get('/schema/testCOxResultsSchema'),
  testCOx01xResults: (id) => apiClient2.get(`/testCorrector01xResults/${id}`),
  testCOx02xResults: (id) => apiClient2.get(`/testCorrector02xResults/${id}`),
  testCOx03xResults: (id) => apiClient2.get(`/testCorrector03xResults/${id}`),
  testCOx04xResults: (id) => apiClient2.get(`/testCorrector04xResults/${id}`),
  testCOx05xResults: (id) => apiClient2.get(`/testCorrector05xResults/${id}`),
  testCOx15xResults: (id) => apiClient2.get(`/testCorrector15xResults/${id}`),
  testCOx16xResults: (id) => apiClient2.get(`/testCorrector16xResults/${id}`),
  testCOx17xResults: (id) => apiClient2.get(`/testCorrector17xResults/${id}`),
  testCOx18xResults: (id) => apiClient2.get(`/testCorrector18xResults/${id}`),
  testCOx20xResults: (id) => apiClient2.get(`/testCorrector20xResults/${id}`),
  testCOx35xResults: (id) => apiClient2.get(`/testCorrector35xResults/${id}`),
  testCOx39xResults: (id) => apiClient2.get(`/testCorrector39xResults/${id}`),
  testCOx41xResults: (id) => apiClient2.get(`/testCorrector41xResults/${id}`),
  testCOx43xResults: (id) => apiClient2.get(`/testCorrector43xResults/${id}`),
  testCOx44xResults: (id) => apiClient2.get(`/testCorrector44xResults/${id}`),
  testCOx45xResults: (id) => apiClient2.get(`/testCorrector45xResults/${id}`),
  testCOx46xResults: (id) => apiClient2.get(`/testCorrector46xResults/${id}`),
  testCOx51xResults: (id) => apiClient2.get(`/testCorrector51xResults/${id}`),
  testCOx52xResults: (id) => apiClient2.get(`/testCorrector52xResults/${id}`),
  testCOx55xResults: (id) => apiClient2.get(`/testCorrector55xResults/${id}`),

  // TestCOx01
  testCOx01Schema: () => apiClient.get('/schema/testCOx01Schema'),
  modelTestCOx01: (id1, id2, id3) => apiClient.get(`/testCorrector01/${id1}/${id2}/${id3}`),
  saveTestCOx01: (model) => apiClient.post(`/testCorrector01`, model),

  // TestCOx02
  testCOx02Schema: () => apiClient.get('/schema/testCOx02Schema'),
  modelTestCOx02: (id1, id2, id3) => apiClient.get(`/testCorrector02/${id1}/${id2}/${id3}`),
  saveTestCOx02: (model) => apiClient.post(`/testCorrector02`, model),

  // TestCOx03
  testCOx03Schema: () => apiClient.get('/schema/testCOx03Schema'),
  modelTestCOx03: (id1, id2, id3) => apiClient.get(`/testCorrector03/${id1}/${id2}/${id3}`),
  saveTestCOx03: (model) => apiClient.post(`/testCorrector03`, model),

  // TestCOx04
  testCOx04Schema: () => apiClient.get('/schema/testCOx04Schema'),
  modelTestCOx04: (id1, id2, id3) => apiClient.get(`/testCorrector04/${id1}/${id2}/${id3}`),
  saveTestCOx04: (model) => apiClient.post(`/testCorrector04`, model),

  // TestCOx05
  testCOx05Schema: () => apiClient.get('/schema/testCOx05Schema'),
  modelTestCOx05: (id1, id2, id3) => apiClient.get(`/testCorrector05/${id1}/${id2}/${id3}`),
  saveTestCOx05: (model) => apiClient.post(`/testCorrector05`, model),

  // TestCOx15
  testCOx15Schema: () => apiClient.get('/schema/testCOx15Schema'),
  modelTestCOx15: (id1, id2, id3) => apiClient.get(`/testCorrector15/${id1}/${id2}/${id3}`),
  saveTestCOx15: (model) => apiClient.post(`/testCorrector15`, model),

  // TestCOx16
  testCOx16Schema: () => apiClient.get('/schema/testCOx16Schema'),
  modelTestCOx16: (id1, id2, id3) => apiClient.get(`/testCorrector16/${id1}/${id2}/${id3}`),
  saveTestCOx16: (model) => apiClient.post(`/testCorrector16`, model),

  // TestCOx17
  testCOx17Schema: () => apiClient.get('/schema/testCOx17Schema'),
  modelTestCOx17: (id1, id2, id3) => apiClient.get(`/testCorrector17/${id1}/${id2}/${id3}`),
  saveTestCOx17: (model) => apiClient.post(`/testCorrector17`, model),

  // TestCOx18
  testCOx18Schema: () => apiClient.get('/schema/testCOx18Schema'),
  modelTestCOx18: (id1, id2, id3) => apiClient.get(`/testCorrector18/${id1}/${id2}/${id3}`),
  saveTestCOx18: (model) => apiClient.post(`/testCorrector18`, model),

  // TestCOx20
  testCOx20Schema: () => apiClient.get('/schema/testCOx20Schema'),
  modelTestCOx20: (id1, id2, id3) => apiClient.get(`/testCorrector20/${id1}/${id2}/${id3}`),
  saveTestCOx20: (model) => apiClient.post(`/testCorrector20`, model),

  // TestCOx35
  testCOx35Schema: () => apiClient.get('/schema/testCOx35Schema'),
  modelTestCOx35: (id1, id2, id3) => apiClient.get(`/testCorrector35/${id1}/${id2}/${id3}`),
  saveTestCOx35: (model) => apiClient.post(`/testCorrector35`, model),

  // TestCOx39
  testCOx39Schema: () => apiClient.get('/schema/testCOx39Schema'),
  modelTestCOx39: (id1, id2, id3) => apiClient.get(`/testCorrector39/${id1}/${id2}/${id3}`),
  saveTestCOx39: (model) => apiClient.post(`/testCorrector39`, model),

  // TestCOx41
  testCOx41Schema: () => apiClient.get('/schema/testCOx41Schema'),
  modelTestCOx41: (id1, id2, id3) => apiClient.get(`/testCorrector41/${id1}/${id2}/${id3}`),
  saveTestCOx41: (model) => apiClient.post(`/testCorrector41`, model),

  // TestCOx43
  testCOx43Schema: () => apiClient.get('/schema/testCOx43Schema'),
  modelTestCOx43: (id1, id2, id3) => apiClient.get(`/testCorrector43/${id1}/${id2}/${id3}`),
  saveTestCOx43: (model) => apiClient.post(`/testCorrector43`, model),

  // TestCOx44
  testCOx44Schema: () => apiClient.get('/schema/testCOx44Schema'),
  modelTestCOx44: (id1, id2, id3) => apiClient.get(`/testCorrector44/${id1}/${id2}/${id3}`),
  saveTestCOx44: (model) => apiClient.post(`/testCorrector44`, model),

  // TestCOx45
  testCOx45Schema: () => apiClient.get('/schema/testCOx45Schema'),
  modelTestCOx45: (id1, id2, id3) => apiClient.get(`/testCorrector45/${id1}/${id2}/${id3}`),
  saveTestCOx45: (model) => apiClient.post(`/testCorrector45`, model),

  // TestCOx46
  testCOx46Schema: () => apiClient.get('/schema/testCOx46Schema'),
  modelTestCOx46: (id1, id2, id3) => apiClient.get(`/testCorrector46/${id1}/${id2}/${id3}`),
  saveTestCOx46: (model) => apiClient.post(`/testCorrector46`, model),

  // TestCOx51
  testCOx51Schema: () => apiClient.get('/schema/testCOx51Schema'),
  modelTestCOx51: (id1, id2, id3) => apiClient.get(`/testCorrector51/${id1}/${id2}/${id3}`),
  saveTestCOx51: (model) => apiClient.post(`/testCorrector51`, model),

  // TestCOx52
  testCOx52Schema: () => apiClient.get('/schema/testCOx52Schema'),
  modelTestCOx52: (id1, id2, id3) => apiClient.get(`/testCorrector52/${id1}/${id2}/${id3}`),
  saveTestCOx52: (model) => apiClient.post(`/testCorrector52`, model),

  // TestCOx55
  testCOx55Schema: () => apiClient.get('/schema/testCOx55Schema'),
  modelTestCOx55: (id1, id2, id3) => apiClient.get(`/testCorrector55/${id1}/${id2}/${id3}`),
  saveTestCOx55: (model) => apiClient.post(`/testCorrector55`, model),

  // proAppointmentsA
  catEventType: () => apiClient.get('/eventType'),
  proAppointmentsASchema: () => apiClient.get('/schema/proAppointmentsASchema'),
  modelProAppointmentsA: (id, dateFocus) => 
  apiClient.get(`/proAppointmentsA/${id}`, { params: { dateFocus } }),
  verifProAppointmentsA: (id, model) => apiClient.post(`/proAppointmentsA/${id}`, model),
  updateProWeeklyPattern: (id, model) =>
  apiClient.put(`/proWeeklyPattern/${id}`, model),
  createProAppointmentsA: (model) =>
    apiClient.post(`/proAppointmentsA`, model),
  updateProAppointmentsA: (id, model) =>
    apiClient.put(`/proAppointmentsA/${id}`, model),
  deleteProAppointmentsA: (id) =>
    apiClient.delete(`/proAppointmentsA/${id}`),

  // proStatisticsA
  proStatisticsASchema: () => apiClient.get('/schema/proStatisticsASchema'),
  modelProStatisticsA: (model) =>
    apiClient.post(`/proStatisticsA`, model),

  // proStatisticsB
  proStatisticsBSchema: () => apiClient.get('/schema/proStatisticsBSchema'),
  modelProStatisticsB: (model) =>
    apiClient.post(`/proStatisticsB`, model),

  // HomePac
  homePacSchema: () => apiClient.get('/schema/homePacSchema'),

  // HomePatLegal
  homePatLegalSchema: () => apiClient.get('/schema/homePatLegalSchema'),

  // PatLogin
  patLoginSchema: () => apiClient.get('/schema/patLoginSchema'),
  patLogin: (token) => apiClient.post('/authPat/login', {}, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }),

  // PatSignUp
  patSignUpSchema: () => apiClient.get('/schema/patSignUpSchema'),
  patSignUp: (model) => apiClient.post('/authPat/register', model ),


  // PatOTP
  patOTPSchema: () => apiClient.get('/schema/patOTPSchema'),

  // PatResPass
  patResPassSchema: () => apiClient.get('/schema/patResPassSchema'),

  // PatHome
  patHomeSchema: () => apiClient.get('/schema/patHomeSchema'),

  // MainHeaderPlatAPat
  mainHeaderPlatAPatSchema: () => apiClient.get('/schema/mainHeaderPlatAPatSchema'),

  // MainHeaderPlatBPat
  mainHeaderPlatBPatSchema: () => apiClient.get('/schema/mainHeaderPlatBPatSchema'),

  // PatLegal
  patLegalSchema: () => apiClient.get('/schema/patLegalSchema'),

  // PatAccount
  patAccountSchema: () => apiClient.get('/schema/patAccountSchema'),
  modelPatAccountPatient: (id) => apiClient.get(`/patientAccount/${id}`),
  patAccountPatient: (id, model) =>
    apiClient.post(`/patientAccount/${id}`, model),

  // InboxPat
  inboxPat: () => apiClient.get('/inboxPat'),

  // PatProfileA
  patProfileASchema: () => apiClient.get('/schema/patProfileASchema'),
  modelPatProfileA: (id, model) =>
    apiClient.post(`/patients/${id}`, model),
  updatePatProfileA: (id, model) =>
    apiClient.put(`/patients/${id}`, model),

  // PatRecordA
  patRecordASchema: () => apiClient.get('/schema/patRecordASchema'),
  recordHCP: (id) => apiClient.get(`/recordHCP/${id}`),
  recordProfes: (id) => apiClient.get(`/recordProfes/${id}`),
  recordTransfers: (id) => apiClient.get(`/permissionsHCP/${id}`),
  saveRecordTransfers: (model) => apiClient.post(`/permissionsHCP`, model),

  // PatRecordB
  patRecordBSchema: () => apiClient.get('/schema/patRecordBSchema'),
  recordDerivations: (id) => apiClient.get(`/patProDerivations/${id}`),
  saveRecordDerivations: (id, model) =>
    apiClient.put(`/patProDerivations/${id}`, model),

  // PatAppointmentsA
  patAppointmentsASchema: () => apiClient.get('/schema/patAppointmentsASchema'),
  modelPatAppointmentsA: (id) => apiClient.get(`/patAppointmentsA/${id}`),
  createPatAppointmentsA: (id, model) =>
    apiClient.post(`/patientAccount/${id}`, model),
  updatePatAppointmentsA: (id, model) =>
    apiClient.post(`/patientAccount/${id}`, model),
  deletePatAppointmentsA: (id) =>
    apiClient.post(`/patientAccount/${id}`),
};
